import { useState } from 'react';
import { createPortal } from 'react-dom';
import { TacticalBoardEditor, type BoardFormationSelection } from '@/features/canvas/TacticalBoardEditor';
import { TacticalBoardView } from '@/features/canvas/TacticalBoardView';
import { useCanvasEnvelope } from '@/features/canvas/useCanvasEnvelope';
import type { BoardScene } from '@/lib/canvas/board-schema';
import type { Player } from '@/lib/types';
import type { ResourceBoardSession } from './resourceEditorSession';

type BoardState = Extract<ReturnType<ResourceBoardSession['getState']>, { status:'available' }>;

/** The existing tactical board; its formation picker and tools sit under it.
 * The player picker offers active roster assignments only. */
export function ResourceStageBoard({ session, state, editable, focus, players }:{
  session:ResourceBoardSession; state:BoardState; editable:boolean; focus:boolean; players:readonly Player[];
}) {
  const envelope = useCanvasEnvelope();
  const [formationTarget, setFormationTarget] = useState<HTMLDivElement | null>(null);
  const [rail, setRail] = useState<HTMLDivElement | null>(null);
  const [formation, setFormation] = useState<BoardFormationSelection | null>(null);
  const scene = state.value.scene;
  const setScene = (next:BoardScene) => {
    const current = session.getState();
    if (current.status === 'available' && current.canEdit) session.change({ ...current.value, scene:next });
  };
  const active = players.filter(player => player.active);
  return <div className="flex flex-col gap-3" data-testid="resource-board-stage">
    <div ref={envelope.setRef} className="relative mx-auto w-full overflow-hidden rounded-panel"
      style={{ aspectRatio:'16 / 9', maxHeight:focus ? 'calc(100dvh - 260px)' : 'min(62vh, calc(100dvh - 360px))',
        maxWidth:focus ? 'calc((100dvh - 260px) * 16 / 9)' : 'calc(min(62vh, calc(100dvh - 360px)) * 16 / 9)' }}>
      {envelope.boardWidth > 0 && (editable
        ? <TacticalBoardEditor appearance="navy" scene={scene} onSceneChange={setScene} width={envelope.boardWidth} players={[...active]}
          formationSelection={{ value:formation, onChange:setFormation }}
          formationControlsSlot={controls => formationTarget ? createPortal(controls, formationTarget) : null}
          toolRailSlot={controls => rail ? createPortal(<div className="flex flex-wrap items-center gap-3 px-2 py-1">{controls}</div>, rail) : null}/>
        : <TacticalBoardView scene={scene} width={envelope.boardWidth} appearance="navy"/>)}
    </div>
    <div hidden={!editable} className="flex flex-wrap items-start gap-3" data-testid="resource-board-tools">
      <div ref={setFormationTarget} className="min-w-[220px]"/>
      <div ref={setRail} role="group" aria-label="Board tools" className="min-w-0 flex-1 rounded-panel border border-rule bg-elevated p-1"/>
    </div>
  </div>;
}
